<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'assignAgent',
  'bulkUpdateField',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();
const agents = useMapGetter('agents/getVerifiedAgents');
const contactAttributes = useMapGetter('attributes/getContactAttributes');

const selectedCount = computed(() => props.selectedContactIds.length);
const showLabelSelector = ref(false);
const showAgentSelector = ref(false);
const agentSearchQuery = ref('');
const showBulkEditPanel = ref(false);
const bulkEditField = ref('');
const bulkEditValue = ref('');

const selectAllLabel = computed(() => {
  return '';
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allItems = computed(() =>
  props.visibleContactIds.map(id => ({
    id,
  }))
);

const selectionModel = computed({
  get: () => new Set(props.selectedContactIds),
  set: newSet => {
    if (!props.visibleContactIds.length) {
      emit('toggleAll', false);
      return;
    }

    const shouldSelectAll =
      newSet.size === props.visibleContactIds.length && newSet.size > 0;
    emit('toggleAll', shouldSelectAll);
  },
});

const filteredAgents = computed(() => {
  const list = agents.value || [];
  const query = agentSearchQuery.value.toLowerCase().trim();
  if (!query) return list;
  return list.filter(
    a =>
      a.name?.toLowerCase().includes(query) ||
      a.email?.toLowerCase().includes(query)
  );
});

const editableFields = computed(() => {
  const fields = [
    { key: 'name', label: t('CONTACTS_LAYOUT.TABLE.NAME') },
    { key: 'email', label: t('CONTACTS_LAYOUT.TABLE.EMAIL') },
    { key: 'phoneNumber', label: t('CONTACTS_LAYOUT.TABLE.PHONE') },
  ];
  const attrs = contactAttributes.value || [];
  attrs.forEach(attr => {
    fields.push({
      key: attr.attributeKey,
      label: attr.attributeDisplayName,
    });
  });
  return fields;
});

const emitClearSelection = () => {
  showLabelSelector.value = false;
  showAgentSelector.value = false;
  showBulkEditPanel.value = false;
  emit('clearSelection');
};

const toggleLabelSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  showAgentSelector.value = false;
  showBulkEditPanel.value = false;
  showLabelSelector.value = !showLabelSelector.value;
};

const closeLabelSelector = () => {
  showLabelSelector.value = false;
};

const handleAssignLabels = labels => {
  emit('assignLabels', labels);
  closeLabelSelector();
};

const toggleAgentSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  showLabelSelector.value = false;
  showBulkEditPanel.value = false;
  agentSearchQuery.value = '';
  showAgentSelector.value = !showAgentSelector.value;
};

const closeAgentSelector = () => {
  showAgentSelector.value = false;
  agentSearchQuery.value = '';
};

const handleAssignAgent = agentId => {
  emit('assignAgent', agentId);
  closeAgentSelector();
};

const toggleBulkEditPanel = () => {
  if (!selectedCount.value || props.isLoading) return;
  showLabelSelector.value = false;
  showAgentSelector.value = false;
  bulkEditField.value = '';
  bulkEditValue.value = '';
  showBulkEditPanel.value = !showBulkEditPanel.value;
};

const closeBulkEditPanel = () => {
  showBulkEditPanel.value = false;
  bulkEditField.value = '';
  bulkEditValue.value = '';
};

const applyBulkEdit = () => {
  if (!bulkEditField.value || !bulkEditValue.value.trim()) return;
  emit('bulkUpdateField', {
    field: bulkEditField.value,
    value: bulkEditValue.value.trim(),
  });
  closeBulkEditPanel();
};
</script>

<template>
  <div
    class="sticky top-0 z-[110] bg-gradient-to-b from-n-background from-90% to-transparent px-6 pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #secondary-actions>
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-2 ml-auto">
          <!-- Assign Labels -->
          <div
            v-on-click-outside="closeLabelSelector"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-tags"
              :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleLabelSelector"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <LabelActions
                v-if="showLabelSelector"
                class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
                @assign="handleAssignLabels"
              />
            </transition>
          </div>

          <!-- Assign Agent -->
          <div
            v-on-click-outside="closeAgentSelector"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-user-check"
              :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleAgentSelector"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showAgentSelector"
                class="absolute right-0 top-full mt-1 w-60 bg-n-solid-2 rounded-lg shadow-lg border border-n-slate-4 z-50 overflow-hidden"
              >
                <div class="px-2 py-1.5 border-b border-n-slate-4">
                  <input
                    v-model="agentSearchQuery"
                    type="text"
                    :placeholder="
                      t('CONTACTS_LAYOUT.TABLE.SEARCH_AGENTS_PLACEHOLDER')
                    "
                    class="w-full px-2 py-1 text-xs bg-n-solid-3 border border-n-weak rounded text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    @click.stop
                  />
                </div>
                <div class="max-h-48 overflow-y-auto py-1">
                  <button
                    type="button"
                    class="w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12"
                    @click="handleAssignAgent(null)"
                  >
                    <Icon
                      icon="i-lucide-user-x"
                      class="size-3.5 flex-shrink-0 text-n-slate-9"
                    />
                    <span>{{ t('CONTACTS_LAYOUT.TABLE.UNASSIGNED') }}</span>
                  </button>
                  <button
                    v-for="agent in filteredAgents"
                    :key="agent.id"
                    type="button"
                    class="w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12"
                    @click="handleAssignAgent(agent.id)"
                  >
                    <Avatar
                      :name="agent.name"
                      :src="agent.avatar_url || agent.avatarUrl"
                      :size="16"
                      rounded-full
                      class="flex-shrink-0"
                    />
                    <span class="truncate flex-1 text-left">{{
                      agent.name
                    }}</span>
                  </button>
                  <div
                    v-if="filteredAgents.length === 0 && agentSearchQuery"
                    class="px-3 py-2 text-xs text-n-slate-10 text-center"
                  >
                    {{ t('CONTACTS_LAYOUT.TABLE.NO_AGENTS_FOUND') }}
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Bulk Edit -->
          <div
            v-on-click-outside="closeBulkEditPanel"
            class="relative flex items-center"
          >
            <Button
              sm
              faded
              slate
              icon="i-lucide-pencil"
              :label="t('CONTACTS_BULK_ACTIONS.BULK_EDIT')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
              @click="toggleBulkEditPanel"
            />
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="showBulkEditPanel"
                class="absolute right-0 top-full mt-1 w-72 bg-n-solid-2 rounded-lg shadow-lg border border-n-slate-4 z-50 overflow-hidden"
              >
                <div class="px-3 py-2 border-b border-n-slate-4">
                  <span class="text-xs font-semibold text-n-slate-12">
                    {{ t('CONTACTS_BULK_ACTIONS.BULK_EDIT_FIELD') }}
                  </span>
                </div>
                <div class="px-3 py-2 space-y-2">
                  <select
                    v-model="bulkEditField"
                    class="w-full px-2 py-1.5 text-xs bg-n-solid-3 border border-n-weak rounded text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  >
                    <option value="" disabled>
                      {{ t('CONTACTS_BULK_ACTIONS.SELECT_FIELD') }}
                    </option>
                    <option
                      v-for="field in editableFields"
                      :key="field.key"
                      :value="field.key"
                    >
                      {{ field.label }}
                    </option>
                  </select>
                  <input
                    v-model="bulkEditValue"
                    type="text"
                    :placeholder="t('CONTACTS_BULK_ACTIONS.ENTER_VALUE')"
                    :disabled="!bulkEditField"
                    class="w-full px-2 py-1.5 text-xs bg-n-solid-3 border border-n-weak rounded text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50"
                    @keydown.enter="applyBulkEdit"
                  />
                  <Button
                    sm
                    faded
                    blue
                    :label="t('CONTACTS_BULK_ACTIONS.APPLY')"
                    :disabled="!bulkEditField || !bulkEditValue.trim()"
                    class="w-full"
                    @click="applyBulkEdit"
                  />
                </div>
              </div>
            </transition>
          </div>

          <!-- Delete -->
          <Policy :permissions="['administrator']">
            <Button
              v-tooltip.bottom="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              sm
              faded
              ruby
              icon="i-lucide-trash"
              :label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :aria-label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-1.5 [&>span:nth-child(2)]:hidden"
              @click="emit('deleteSelected')"
            />
          </Policy>
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
